import { type DeviceProfile, mergeRecursive } from "@novorender/api";
import { useEffect, useRef } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectDeviceProfile } from "features/render";

function shallowEqualDeviceProfile(a: DeviceProfile, b: DeviceProfile): boolean {
    if (a === b) {
        return true;
    }

    return (
        a.tier === b.tier &&
        a.detailBias === b.detailBias &&
        a.renderResolution === b.renderResolution &&
        a.framerateTarget === b.framerateTarget &&
        a.features.outline === b.features.outline &&
        a.limits.maxGPUBytes === b.limits.maxGPUBytes &&
        a.limits.maxPrimitives === b.limits.maxPrimitives &&
        a.limits.maxSamples === b.limits.maxSamples &&
        a.materialTextureRendering === b.materialTextureRendering &&
        a.materialTextureResolution === b.materialTextureResolution
    );
}

export function useSyncDeviceProfile() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const profile = useAppSelector(selectDeviceProfile);
    const prevProfileRef = useRef<DeviceProfile | undefined>();

    useEffect(() => {
        if (!profile || !view) {
            return;
        }

        const { autofreezeEnabled: _autofreezeEnabled, ...viewDeviceProfile } = profile;
        const mergedProfile: DeviceProfile = mergeRecursive(view.deviceProfile, viewDeviceProfile);

        const prevProfile = prevProfileRef.current;
        if (prevProfile && shallowEqualDeviceProfile(prevProfile, mergedProfile)) {
            return;
        }

        view?.updateDeviceProfile(mergedProfile);
        prevProfileRef.current = mergedProfile;
    }, [profile, view]);
}
